import { fetchBookingLogs } from "app/actions/BookingLog";
import { BookingLogsModel } from "app/models";
import { useActions } from "app/utils/hooks";
import { useCallback, useEffect, useState } from "react";
import I18n from "app/i18n";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { DateTime } from "luxon";

export type GroupBookingLogsTypes = {
  today: BookingLogsModel[];
  thisWeek: BookingLogsModel[];
  thisMonth: BookingLogsModel[];
  everythingElse: BookingLogsModel[];
};

const bookingLogSortDate = (date: string) => {
  if (!date) {
    return null;
  }

  const now = DateTime.now();
  const bookingLogDate = DateTime.fromISO(date);

  const diffInDays = now.diff(bookingLogDate, "days").days;

  if (diffInDays < 1) {
    return I18n.t("Today");
  }

  if (diffInDays < 7) {
    return I18n.t("This Week");
  }

  if (diffInDays < 30) {
    return I18n.t("This Month");
  }

  return I18n.t("Everything Else");
};

export const useBookingLogsModel = (logsQuery) => {
  const bookingLogAction = useActions({
    fetchBookingLogs,
  });

  const defaultGroupBookingLogs: GroupBookingLogsTypes = {
    today: [],
    thisWeek: [],
    thisMonth: [],
    everythingElse: [],
  };

  const [groupedBookingLogs, setGroupedBookingLogs] =
    useState<GroupBookingLogsTypes>(defaultGroupBookingLogs);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const groupBookingLogsByDate = useCallback(
    (bookingLogs: BookingLogsModel[]) => {
      const grouped: GroupBookingLogsTypes = {
        today: [],
        thisWeek: [],
        thisMonth: [],
        everythingElse: [],
      };

      bookingLogs.forEach((bookingLog: BookingLogsModel) => {
        const label = bookingLogSortDate(bookingLog.createdAt);

        if (label === "Today") {
          grouped.today.push(bookingLog);
        } else if (label === "This Week") {
          grouped.thisWeek.push(bookingLog);
        } else if (label === "This Month") {
          grouped.thisMonth.push(bookingLog);
        } else {
          grouped.everythingElse.push(bookingLog);
        }
      });

      return grouped;
    },
    []
  );

  const loadData = useCallback(async (fetchPage?: number) => {
    setLoading(true);
    try {
      const response: {students: BookingLogsModel[], headers: any} =
        await bookingLogAction.fetchBookingLogs({
          ...logsQuery,
          page: fetchPage ? fetchPage : currentPage,
        });

      if (response?.students.length > 0) {
        // Remove duplicates by ID
        const uniqueLogs = response.students.filter((log, index, self) =>
          index === self.findIndex((l) => l.id === log.id)
        );

        const newGroupedLogs = groupBookingLogsByDate(uniqueLogs);

        if (response.students.length < logsQuery.perPage) {
          setHasMore(false);
        }

        setCurrentPage(fetchPage ? fetchPage + 1 : currentPage + 1);

        setGroupedBookingLogs((prev) => ({
          today: [...prev.today, ...newGroupedLogs.today],
          thisWeek: [...prev.thisWeek, ...newGroupedLogs.thisWeek],
          thisMonth: [...prev.thisMonth, ...newGroupedLogs.thisMonth],
          everythingElse: [
            ...prev.everythingElse,
            ...newGroupedLogs.everythingElse,
          ],
        }));
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [currentPage, logsQuery, groupBookingLogsByDate]);

  const [bookingLogsContainer] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadData,
    disabled: false,
    rootMargin: "0px 0px 200px 0px",
  });

  useEffect(() => {
    setGroupedBookingLogs(defaultGroupBookingLogs);
    setHasMore(true);
    setCurrentPage(1);
    loadData(1);
  }, [logsQuery.from, logsQuery.to, logsQuery.user_ids, logsQuery.log_types]);

  const nullData: boolean = Object?.values(groupedBookingLogs).every(
    (arr): boolean => Array.isArray(arr) && arr.length === 0
  );

  return {
    groupedBookingLogs,
    loading,
    nullData,
    bookingLogsContainer,
  };
};
